import { useEffect, useRef, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { RotateCcw, Play, Loader2, History, Volume2, Square, Plus, Trash2, ArrowUp, ArrowDown, Wand2, Upload, Download, FileText, Gauge, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const TRAITS = [
  { key: "warmth", label: "Warmth", lo: "Reserved", hi: "Tender" },
  { key: "curiosity", label: "Curiosity", lo: "Answers", hi: "Asks back" },
  { key: "formality", label: "Formality", lo: "Casual", hi: "Formal" },
  { key: "directness", label: "Directness", lo: "Suggestive", hi: "Plain" },
  { key: "poetic", label: "Register", lo: "Prosaic", hi: "Poetic" },
  {
    key: "engagement",
    label: "Engagement",
    lo: "Answers and steps back",
    hi: "Digs into your process",
    note: "Turned up, Griot repeats your own words back, asks for the concrete detail, names patterns across the conversation and never closes a turn. It also speaks a little longer — up to 70 words — so replies take about half a second more.",
  },
  {
    key: "humor",
    label: "Humour / trickster",
    lo: "Straight",
    hi: "Cheeky trickster",
    note: "The cheeky companion: playful, a little mischievous, curious, wise under the wit. It teases ideas, never the person, varies its cadence — a playful turn or two, then something plain — and goes completely straight if the listener is upset or serious.",
  },
];

const SETTING_KEYS = [
  "daily_cap_usd", "monthly_cap_usd", "turns_per_session", "top_k",
  "chat_model", "stt_model", "tts_model", "tts_voice", "voice_direction", "voice_direction_female", "code_switch", "tradition", "language",
  "voice_choice", "voice_male", "voice_female",
  "realtime", "tts_provider", "eleven_voice_id", "eleven_voice_name", "eleven_model", "eleven_stability", "eleven_similarity", "eleven_speed",
  "eleven_voice_male", "eleven_voice_male_name", "eleven_voice_male_preview", "eleven_voice_female", "eleven_voice_female_name", "eleven_voice_female_preview",
  "eleven_trim_male", "eleven_trim_female",
  "show_sources", "sources_open", "show_transcript", "allow_text", "show_notes", "note_motion", "ask_name", "show_greeting", "greeting", "greeting_short", "greetings", "greetings_en",
];

// The two sides of the room: the setting that holds each voice, and the note that performer is handed.
const SIDES = [
  ["male", "A man's voice", "voice_male", "voice_direction"],
  ["female", "A woman's voice", "voice_female", "voice_direction_female"],
];

// The library pair, which always travels together: he is his, she is hers.
const LIBRARY = [
  ["male", "his", "Real Kenyan man, 30s-40s — East African English, warm baritone", "eleven_voice_male", "eleven_voice_male_name", "eleven_voice_male_preview", "voice_direction"],
  ["female", "her", "Real Kenyan woman, young — smooth East African, Nairobi", "eleven_voice_female", "eleven_voice_female_name", "eleven_voice_female_preview", "voice_direction_female"],
];

// Studio was one endless scroll of twelve cards. Now it is four rooms.
const TABS = [
  ["persona", "Persona", "Who he is: sliders, brief, what he absorbed, and a sandbox"],
  ["voice", "Voice", "Who speaks, how they speak it, and the models behind it"],
  ["welcome", "Welcome", "How he opens, and the questions offered on the way in"],
  ["limits", "Limits & privacy", "Caps, and what participants see on screen"],
];

// which of the two columns hold anything in each room
const COLS = {
  persona: { left: true, right: true },
  voice: { left: true, right: false },
  welcome: { left: false, right: true },
  limits: { left: false, right: true },
};

const VOICE_NOTES = {
  cedar: "Deepest and oldest-reading — best base for an elder",
  ash: "Deep, grainy, earthy — ages well with direction",
  marin: "Warmest and most detailed, but middle-aged",
  sage: "Calm, considered, a touch dry — forties",
  ballad: "Lyrical, rises and falls — leans storyteller",
  verse: "Expressive and theatrical",
  alloy: "Neutral and even — the plain default",
  echo: "Soft, airy, close to the mic",
  coral: "Bright, forward, quick — reads young",
  shimmer: "Light and breathy — reads young",
};

const Card = ({ title, note, right, children, testId, show = true }) => (show ? (
  <section className="min-w-0 break-words rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4 sm:p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-base font-medium">{title}</h3>
        {note && <p className="mt-0.5 max-w-xl text-xs text-[#6E7582]">{note}</p>}
      </div>
      {right}
    </div>
    <div className="mt-5">{children}</div>
  </section>
) : null);

const Num = ({ label, value, suffix, testId, onChange }) => (
  <label className="block">
    <span className="text-xs text-[#6E7582]">{label}</span>
    <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-white/[0.03] px-3">
      <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent py-2.5 font-mono text-sm text-[#F3EFE6] focus:outline-none" />
      <span className="whitespace-nowrap text-xs text-[#6E7582]">{suffix}</span>
    </div>
  </label>
);

const Pick = ({ label, value, options, testId, onChange }) => (
  <label className="block">
    <span className="text-xs text-[#6E7582]">{label}</span>
    <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2.5 font-mono text-sm text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const Toggle = ({ label, note, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 border-t border-white/[0.06] py-3 first:border-0 first:pt-0">
    <span className="text-sm text-[#D8D2C4]">{label}<span className="mt-0.5 block text-xs text-[#6E7582]">{note}</span></span>
    <Switch data-testid={testId} checked={!!checked} onCheckedChange={onChange} className="mt-1 data-[state=checked]:bg-[#E07A5F]" />
  </div>
);

export default function Studio() {
  const [data, setData] = useState(null);
  const [p, setP] = useState(null);
  const [suggestingWelcome, setSuggestingWelcome] = useState(false);
  const [s, setS] = useState(null);
  const [opts, setOpts] = useState(null);
  const [models, setModels] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [ways, setWays] = useState(null);
  const [waysCustom, setWaysCustom] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [q, setQ] = useState("What did participants say about belonging?");
  const [out, setOut] = useState(null);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState("");
  const [prompt, setPrompt] = useState("");
  const [brief, setBrief] = useState("");
  const [dragging, setDragging] = useState(false);
  const [probing, setProbing] = useState(false);
  const [chatProbe, setChatProbeState] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);
  const [showHistory, setShowHistory] = useState(false);
  const [briefFiles, setBriefFiles] = useState([]);
  const [briefSaved, setBriefSaved] = useState("saved");
  const [uploadingBrief, setUploadingBrief] = useState(false);
  const briefLoaded = useRef(false);
  const [dig, setDig] = useState(null);
  const [redrawing, setRedrawing] = useState(false);
  const [plan, setPlan] = useState(null);     // what the ElevenLabs plan allows right now
  const [tab, setTab] = useState("persona");
  const cols = COLS[tab] || COLS.persona;



  const redraw = async () => {
    setRedrawing(true);
    try {
      setDig(await api.refreshDigest());
      toast.success("Griot has re-read the Lab's memory");
    } catch (e) { toast.error(errMsg(e)); } finally { setRedrawing(false); }
  };
  const [publishing, setPublishing] = useState(false);

  const load = () => api.persona().then((d) => {
    setData(d);
    setP((cur) => cur || d.live.traits);
  });

  useEffect(() => {
    load();
    api.settings().then(setS);
    api.getBrief().then((b) => { setBrief(b.text || ""); setBriefFiles(b.files || []); briefLoaded.current = true; }).catch(() => { briefLoaded.current = true; });
    api.digest().then(setDig).catch(() => {});
    api.voiceOptions().then(setOpts).catch(() => {});
    api.voiceLab().then((d) => setPlan(d?.quota
      ? { ...d.quota, paid: !!d.quota.can_design, down: d.down_since || null, reason: d.down_reason || "" }
      : null)).catch(() => {});
    api.models().then(setModels).catch(() => {});
    api.metrics().then(setMetrics).catch(() => {});
    api.adminOpenings().then((r) => { setWays(r.items); setWaysCustom(r.custom); }).catch(() => setWays([]));
  }, []);
  useEffect(() => { if (p) api.promptPreview(p, brief).then((r) => setPrompt(r.prompt)).catch(() => {}); }, [p, brief]);

  useEffect(() => {                         // the brief saves itself — it is never only in the browser
    if (!briefLoaded.current) return;
    setBriefSaved("saving");
    const t = setTimeout(() => {
      api.saveBrief(brief).then(() => setBriefSaved("saved")).catch(() => setBriefSaved("error"));
    }, 800);
    return () => clearTimeout(t);
  }, [brief]);

  const trait = (k) => ([v]) => setP((x) => ({ ...x, [k]: v }));
  const setWelcomes = (items, key = "greetings") => setS((x) => ({ ...x, [key]: items }));
  const setWelcome = (i, v, key = "greetings") => setS((x) => ({ ...x, [key]: (x[key] || []).map((g, j) => (j === i ? v : g)) }));

  const suggestWelcomes = async (language = "auto") => {
    setSuggestingWelcome(true);
    try {
      const r = await api.suggestWelcomes(4, language);
      setWelcomes(r.items || [], language === "en" ? "greetings_en" : "greetings");
      toast("Drafted from this Lab's memory — edit, then publish");
    } catch (e) { toast.error(errMsg(e)); }
    setSuggestingWelcome(false);
  };
  const num = (k) => (v) => setS((x) => ({ ...x, [k]: v === "" ? "" : Number(v) }));
  const field = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const pset = (next) => setS((x) => ({ ...x, ...next }));
  // Who physically speaks. The real Kenyan voices unless they have been switched off or have run dry.
  const realVoice = (s?.tts_provider || "elevenlabs") === "elevenlabs";

  const wakeVoice = async () => {
    try {
      const r = await api.voiceWake();
      setPlan((x) => (x ? { ...x, down: null, reason: "", left: r.left ?? x.left } : x));
      toast.success("The real voice has the microphone again");
    } catch (e) { toast.error(errMsg(e)); }
  };

  // the library voices' own preview clips are public files — no API call, no plan needed
  const hearUrl = (url, tag) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      const was = playing;
      setPlaying(null);
      if (was === tag) return;
    }
    if (!url) return;
    const el = new Audio(url);
    el.onended = () => { audioRef.current = null; setPlaying(null); };
    el.onerror = () => { audioRef.current = null; setPlaying(null); toast.error("That clip would not load"); };
    audioRef.current = el;
    setPlaying(tag);
    el.play().catch(() => { setPlaying(null); });
  };

  // the real voice, synthesised now with this Lab's settings — this one spends characters
  const hearReal = async (voiceId, tag) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      const was = playing;
      setPlaying(null);
      if (was === tag) return;
    }
    if (!voiceId) return toast.error("No voice id set for that side");
    setLoadingVoice(tag);
    try {
      const r = await api.voiceLabPreview(voiceId, "Hujambo, karibu nyumbani. Sit down, my friend — there is time.");
      const el = new Audio(r.audio_url);
      el.onended = () => { audioRef.current = null; setPlaying(null); };
      audioRef.current = el;
      setPlaying(tag);
      await el.play();
    } catch (e) { toast.error(errMsg(e)); setPlaying(null); } finally { setLoadingVoice(null); }
  };

  const run = async () => {
    setRunning(true);
    try { setOut(await api.sandbox(p, q, brief)); } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  const readBrief = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Keep brief documents under 20 MB");
    setUploadingBrief(true);
    try {
      const r = await api.addBriefFile(file);
      setBriefFiles(r.files || []);
      toast.success(`${file.name} added to the brief`);
    } catch (e) { toast.error(errMsg(e)); } finally { setUploadingBrief(false); }
  };

  const dropBriefFile = async (f) => {
    try {
      const r = await api.delBriefFile(f.id);
      setBriefFiles(r.files || []);
      toast(`Removed ${f.name}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const grabBriefFile = async (f) => {
    try {
      const blob = await api.downloadBriefFile(f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const hear = async (text, voice, direction, tag) => {
    const v = voice || s.tts_voice;
    const id = tag || v;
    if (audioRef.current) {               // whatever is playing stops first — never two at once
      audioRef.current.pause();
      audioRef.current = null;
      const was = playing;
      setPlaying(null);
      if (was === id) return;             // pressing the same one again means stop
    }
    setLoadingVoice(id);
    try {
      const r = await api.voicePreview(v, s.language, s.tts_model, text || (s.greetings?.[0] || s.greeting), direction !== undefined ? direction : s.voice_direction);
      const el = new Audio(`data:audio/mp3;base64,${r.audio_b64}`);
      el.onended = () => { audioRef.current = null; setPlaying(null); };
      audioRef.current = el;
      setPlaying(id);
      await el.play();
    } catch (e) { toast.error(errMsg(e)); setPlaying(null); } finally { setLoadingVoice(null); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const settings = SETTING_KEYS.reduce((a, k) => ({ ...a, [k]: s[k] }), {});
      const v = await api.publishPersona({ traits: p, brief, note: note || "Published from Studio", settings });
      await api.saveOpenings((ways || []).filter((w) => w.trim()));
      setWaysCustom(true);
      toast.success(`Published v${v.version} — live for new sessions`);
      setNote("");
      load();
      api.settings().then(setS);
    } catch (e) { toast.error(errMsg(e)); } finally { setPublishing(false); }
  };

  const rollback = async (v) => {
    try {
      await api.rollback(v.id);
      toast(`Rolled back to v${v.version} — persona and configuration`);
      setP(v.traits);
      setShowHistory(false);
      load();
      api.settings().then(setS);
      api.getBrief().then((b) => { setBrief(b.text || ""); setBriefFiles(b.files || []); }).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.suggestOpenings(Math.max(3, (ways || []).length || 3));
      if (!r.items?.length) return toast("Nothing in this Lab's memory yet — upload documents first");
      setWays(r.items.slice(0, 5));
      toast("Drafted from this Lab's memory — edit, then publish");
    } catch (e) { toast.error(errMsg(e)); } finally { setSuggesting(false); }
  };

  const probe = async () => {
    setProbing(true);
    try {
      const r = await api.probeModel(s.chat_model);
      setChatProbeState(r);
      if (r.ok === false) toast.error(`${s.chat_model} refused a test call`);
    } catch (e) { toast.error(errMsg(e)); } finally { setProbing(false); }
  };

  const setWay = (i, v) => setWays((w) => w.map((x, j) => (j === i ? v : x)));
  const moveWay = (i, d) => setWays((w) => {
    const n = [...w];
    const j = i + d;
    if (j < 0 || j >= n.length) return n;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  if (!data || !p || !s || ways === null) return <p className="text-sm text-[#6E7582]">Loading…</p>;

  const spent = metrics?.month_spend ?? 0;
  const nextV = data.versions.length ? data.versions[0].version + 1 : 1;
  const probes = models?.probes || {};
  const label = (m) => (probes[m]?.ms ? `${m} · ${(probes[m].ms / 1000).toFixed(1)}s${probes[m].ok === false ? " · refused" : ""}` : m);
  const probeInfo = chatProbe?.model === s.chat_model ? chatProbe : probes[s.chat_model];

  return (
    <div className="space-y-5" data-testid="personality-studio">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Studio</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">How Griot behaves in this Lab</h2>
          <p className="mt-1 text-sm text-[#6E7582]">Persona, voice, models, questions and limits — published together as v{data.live.version}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="studio-history-btn" onClick={() => setShowHistory(true)} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6]">
            <History size={14} /> History
          </button>
          <input data-testid="persona-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Release note" className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-[#F3EFE6] placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none" />
          <button data-testid="persona-publish-btn" onClick={publish} disabled={publishing} className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-4 py-2 text-sm font-semibold text-[#121417] hover:bg-[#F4A261] disabled:opacity-60">
            {publishing && <Loader2 size={14} className="animate-spin" />} Publish v{nextV}
          </button>
        </div>
      </header>

      <nav
        className="sticky top-0 z-20 -mx-1 flex gap-1 overflow-x-auto border-b border-white/[0.06] bg-[#0F1114]/95 px-1 py-2 backdrop-blur"
        data-testid="studio-tabs"
      >
        {TABS.map(([k, label, hint]) => (
          <button
            key={k}
            data-testid={`studio-tab-${k}`}
            aria-pressed={tab === k}
            onClick={() => setTab(k)}
            title={hint}
            className={`shrink-0 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${tab === k ? "bg-[#E07A5F]/15 text-[#F4A261]" : "text-[#6E7582] hover:text-[#A1A8B3]"}`}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="grid gap-5 xl:grid-cols-5">
        <div className={`min-w-0 space-y-5 ${cols.left ? (cols.right ? "xl:col-span-2" : "xl:col-span-5") : "hidden"}`}>
          <Card show={tab === "persona"} title="Persona" note="Sliders rewrite the system prompt. No code, no prompt editing." testId="persona-traits">
            <div className="space-y-6">
              {TRAITS.map((t) => (
                <div key={t.key} data-testid={`personality-${t.key}-slider`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium">{t.label}</span>
                    <span className="font-mono text-xs text-[#F4A261]" data-testid={`personality-${t.key}-value`}>{p[t.key]}</span>
                  </div>
                  <Slider value={[p[t.key]]} onValueChange={trait(t.key)} max={100} step={1} className="mt-3 [&_[role=slider]]:border-[#E07A5F] [&_[role=slider]]:bg-[#121417] [&_.bg-primary]:bg-[#E07A5F]" />
                  <div className="mt-1.5 flex justify-between text-[11px] text-[#6E7582]"><span>{t.lo}</span><span>{t.hi}</span></div>
                  {t.note && <p className="mt-2 text-[11px] leading-relaxed text-[#6E7582]">{t.note}</p>}
                </div>
              ))}
              <details>
                <summary className="cursor-pointer text-xs text-[#6E7582] hover:text-[#A1A8B3]">Preview generated system prompt</summary>
                <pre data-testid="prompt-preview" className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl border border-white/[0.06] bg-[#121417] p-3 font-mono text-[11px] leading-relaxed text-[#A1A8B3]">{prompt}</pre>
              </details>
            </div>
          </Card>

          <Card
            show={tab === "persona"}
            title="Character brief"
            note="Who Griot is in this Lab. Paste the text, attach documents, or both — it saves itself as you type and survives every publish."
            testId="persona-brief"
            right={
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6]">
                {uploadingBrief ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Attach document
                <input data-testid="persona-brief-file" type="file" accept=".md,.markdown,.txt,.pdf,.docx,.rtf,.html,.htm,.vtt,.srt,.csv" className="hidden" onChange={(e) => { readBrief(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            }
          >
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); readBrief(e.dataTransfer.files?.[0]); }}
              className={`rounded-xl border transition-colors ${dragging ? "border-[#E07A5F] bg-[#E07A5F]/10" : "border-white/10 bg-white/[0.03]"}`}
            >
              <textarea
                data-testid="persona-brief-input"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={10}
                maxLength={12000}
                placeholder={"GRIOT\n\nCharacter & Personality Definition\n\nAn elder storyteller, pattern-reader, guide…\n\n(or drop a document anywhere in this box)"}
                className="w-full resize-y bg-transparent px-4 py-3 font-mono text-[12px] leading-relaxed text-[#F3EFE6] placeholder:text-[#4F5560] focus:outline-none"
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <span data-testid="brief-save-state" className={`font-mono text-[11px] ${briefSaved === "error" ? "text-[#E63946]" : briefSaved === "saving" ? "text-[#E9C46A]" : "text-[#2EC4B6]"}`}>
                {briefSaved === "error" ? "could not save" : briefSaved === "saving" ? "saving…" : "saved"}
              </span>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] text-[#6E7582]" data-testid="persona-brief-count">{brief.length}/12000</span>
                {brief && (
                  <button data-testid="persona-brief-clear" onClick={() => setBrief("")} className="font-mono text-[11px] text-[#6E7582] underline hover:text-[#E63946]">clear</button>
                )}
              </div>
            </div>

            {briefFiles.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="brief-files">
                {briefFiles.map((f) => (
                  <div key={f.id} data-testid={`brief-file-${f.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <FileText size={14} className="shrink-0 text-[#6E7582]" />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-[#F3EFE6]">{f.name}</span>
                    <span className="shrink-0 font-mono text-[11px] text-[#6E7582]">{f.chars.toLocaleString()} chars</span>
                    <button data-testid={`brief-file-get-${f.id}`} onClick={() => grabBriefFile(f)} title="Download the original" className="shrink-0 rounded-lg p-1.5 text-[#A1A8B3] hover:bg-white/[0.05]"><Download size={13} /></button>
                    <button data-testid={`brief-file-del-${f.id}`} onClick={() => dropBriefFile(f)} title="Remove from the brief" className="shrink-0 rounded-lg p-1.5 text-[#A1A8B3] hover:bg-white/[0.05] hover:text-[#E63946]"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-4 max-w-xl text-xs text-[#6E7582]">
              Attached documents are read into the brief alongside the text above and the originals are kept. The sliders always win over the brief, and the grounding rules, PII protection and the spoken style can't be overridden by it. Versions keep a copy for the record, but rolling back never overwrites what's here.
            </p>
          </Card>

          <Card
            show={tab === "persona"}
            title="What Griot absorbed"
            note="Read once from everything in Memory and carried into every conversation — so Griot talks from the material instead of quoting it. Redrawn automatically when Memory changes."
            testId="archive-digest"
            right={
              <button data-testid="digest-refresh-btn" onClick={redraw} disabled={redrawing} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6] disabled:opacity-60">
                {redrawing ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Redraw
              </button>
            }
          >
            {dig?.text ? (
              <>
                <p data-testid="digest-text" className="max-h-72 overflow-auto whitespace-pre-wrap rounded-xl border border-white/[0.06] bg-[#121417] p-4 text-[12px] leading-relaxed text-[#A1A8B3]">{dig.text}</p>
                <p className="mt-2 font-mono text-[11px] text-[#6E7582]">read from {dig.chunks} passage{dig.chunks === 1 ? "" : "s"}{dig.archive_chunks !== dig.chunks ? ` · Memory now holds ${dig.archive_chunks} — redraw to catch up` : ""}</p>
              </>
            ) : (
              <p className="text-xs text-[#6E7582]">Nothing read yet. Upload documents in Memory, then press Redraw.</p>
            )}
          </Card>

          <Card
            show={tab === "voice"}
            title="The voice"
            note="Two real Kenyan voices, and the visitor picks one at the door. Griot writes the words; they say them."
            testId="voice-language-panel"
          >
            <div className="flex items-start justify-between gap-4 rounded-xl border border-white/[0.06] bg-[#121417] p-4">
              <div>
                <p className="text-sm text-[#D8D2C4]">Let the visitor choose</p>
                <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
                  On when the sit-down screen offers "a man's voice" or "a woman's voice". Off and everyone gets
                  whoever is cast as his.
                </p>
              </div>
              <Switch
                data-testid="voice-choice-toggle"
                checked={s.voice_choice !== false}
                onCheckedChange={field("voice_choice")}
                style={s.voice_choice !== false ? { backgroundColor: "#E07A5F" } : undefined}
              />
            </div>

            {plan && (
              <div className="mt-3 grid gap-2" data-testid="voice-plan-note">
                <p
                  className="flex items-start gap-2 rounded-xl border px-4 py-3 text-xs leading-relaxed"
                  style={plan.paid
                    ? { borderColor: "rgba(255,255,255,0.06)", color: "#6E7582" }
                    : { borderColor: "rgba(244,162,97,0.3)", background: "rgba(244,162,97,0.06)", color: "#A1A8B3" }}
                >
                  <AlertTriangle size={13} className="mt-0.5 shrink-0 text-[#F4A261]" style={plan.paid ? { opacity: 0.45 } : undefined} />
                  {plan.paid ? (
                    <span>
                      ElevenLabs plan <span className="font-mono text-[#D8D2C4]">{plan.tier}</span> —{" "}
                      <span className="text-[#D8D2C4]">{(plan.left ?? 0).toLocaleString()} characters left</span>, roughly{" "}
                      {plan.sittings_left} more ten-minute sittings. When they run out the understudy takes over on its
                      own, so the Lab keeps talking.
                    </span>
                  ) : (
                    <span>
                      The ElevenLabs account is on the <span className="font-mono text-[#D8D2C4]">free</span> tier, and a
                      free account is not allowed to speak with library voices. Keith and Yna stay silent until the
                      <a href="https://elevenlabs.io/pricing" target="_blank" rel="noreferrer" className="mx-1 text-[#F4A261] underline">Starter plan</a>
                      is active.
                    </span>
                  )}
                </p>
                {plan.down && (
                  <p
                    className="flex flex-wrap items-center gap-2 rounded-xl border px-4 py-3 text-xs leading-relaxed"
                    data-testid="voice-down-note"
                    style={{ borderColor: "rgba(224,122,95,0.4)", background: "rgba(224,122,95,0.08)", color: "#E6C9BC" }}
                  >
                    <AlertTriangle size={13} className="shrink-0 text-[#E07A5F]" />
                    <span className="min-w-0 flex-1">
                      The understudy currently has the microphone — {plan.reason || "the real voice refused a sentence"}.
                      Top up at ElevenLabs, then hand it back.
                    </span>
                    <button
                      data-testid="voice-wake-btn"
                      onClick={wakeVoice}
                      className="rounded-full border border-[#E07A5F] px-3 py-1 text-[11px] text-[#F4A261] hover:bg-[#E07A5F]/15"
                    >
                      Hand it back
                    </button>
                  </p>
                )}
              </div>
            )}

            <p className="mt-6 text-sm font-medium text-[#D8D2C4]">1 — Who speaks</p>
            <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
              Two real Kenyan people. Griot decides what to say and they say it — so the accent is not an
              impression, it is somebody's actual voice. The visitor picks one when they sit down.
            </p>

            <div className="mt-3 flex items-start justify-between gap-4 rounded-xl border border-white/[0.06] bg-[#121417] p-4">
              <div>
                <p className="text-sm text-[#D8D2C4]">Use the real voices</p>
                <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
                  On, Keith and Yna speak every word. Off, the understudy below does all the talking — faster by
                  about half a second, but an American voice doing a Kenyan accent.
                </p>
              </div>
              <Switch
                data-testid="voice-engine-toggle"
                checked={realVoice}
                onCheckedChange={(on) => pset({ tts_provider: on ? "elevenlabs" : "openai", realtime: true })}
                style={realVoice ? { backgroundColor: "#E07A5F" } : undefined}
              />
            </div>

            <div className="mt-3 grid gap-2" data-testid="voice-library">
              {LIBRARY.map(([who, word, note, idKey, nameKey, previewKey]) => {
                const tag = `lib-${who}`;
                const liveTag = `libreal-${who}`;
                return (
                  <div
                    key={who}
                    data-testid={`voice-library-row-${who}`}
                    className={`min-w-0 rounded-xl border px-3 py-3 transition-colors ${realVoice ? "border-[#E07A5F]/70 bg-[#E07A5F]/[0.08]" : "border-white/10 bg-white/[0.03]"}`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`voice-library-hear-${who}`}
                        onClick={() => hearUrl(s[previewKey], tag)}
                        disabled={!s[previewKey]}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#E07A5F]/50 text-[#F4A261] hover:bg-[#E07A5F]/15 disabled:opacity-40"
                        aria-label={playing === tag ? "Stop" : `Hear ${s[nameKey]}`}
                      >
                        {playing === tag ? <Square size={12} /> : <Volume2 size={14} />}
                      </button>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] text-[#F3EFE6]">{s[nameKey] || "—"}</span>
                        <span className="block text-[11px] leading-snug text-[#6E7582]">{note}</span>
                      </span>
                      <span
                        className="shrink-0 rounded-full border border-[#E07A5F] bg-[#E07A5F]/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#F4A261]"
                        data-testid={`voice-library-side-${who}`}
                      >
                        {word}
                      </span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <button
                        data-testid={`voice-library-live-${who}`}
                        onClick={() => hearReal(s[idKey], liveTag)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 text-[11px] text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261]"
                      >
                        {loadingVoice === liveTag ? <Loader2 size={12} className="animate-spin" /> : playing === liveTag ? <Square size={11} /> : <Volume2 size={12} />}
                        Hear them live
                      </button>
                      <input
                        data-testid={`voice-library-name-${who}`}
                        value={s[nameKey] || ""}
                        onChange={(e) => field(nameKey)(e.target.value)}
                        placeholder="Name"
                        className="w-28 min-w-0 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-[#D8D2C4] focus:border-[#E07A5F] focus:outline-none"
                      />
                      <input
                        data-testid={`voice-library-id-${who}`}
                        value={s[idKey] || ""}
                        onChange={(e) => field(idKey)(e.target.value)}
                        placeholder="ElevenLabs voice id"
                        className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 font-mono text-[10.5px] text-[#6E7582] focus:border-[#E07A5F] focus:outline-none"
                      />
                    </div>
                    {realVoice && (
                      <label className="mt-2.5 flex items-center gap-3" data-testid={`voice-library-trim-${who}`}>
                        <span className="shrink-0 text-[11px] text-[#A1A8B3]">Loudness</span>
                        <input
                          data-testid={`voice-trim-${who}`}
                          type="range"
                          min={0.5}
                          max={2.4}
                          step={0.01}
                          value={s[`eleven_trim_${who}`] ?? 1}
                          onChange={(e) => field(`eleven_trim_${who}`)(Number(e.target.value))}
                          className="min-w-0 flex-1 accent-[#E07A5F]"
                        />
                        <span className="w-10 shrink-0 text-right font-mono text-[10px] text-[#D8D2C4]">
                          {(s[`eleven_trim_${who}`] ?? 1).toFixed(2)}×
                        </span>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            {realVoice && (
              <>
              <p className="mt-2 text-[11px] leading-relaxed text-[#6E7582]" data-testid="voice-trim-note">
                Two voices from the same library are rarely the same loudness — measured over three lines each,
                {" "}{s.eleven_voice_female_name || "the woman's voice"} came out of ElevenLabs seven decibels
                quieter than {s.eleven_voice_male_name || "the man's voice"}. Loudness is the correction that
                brings them to the same presence in the room. Retune it if you cast a new voice.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="voice-library-settings">
                {[["eleven_speed", "Speed", 0.7, 1.2, 0.01, 0.92], ["eleven_stability", "Steadiness", 0, 1, 0.05, 0.45], ["eleven_similarity", "Character", 0, 1, 0.05, 0.75]].map(([k, label, min, max, step, dflt]) => (
                  <label key={k} className="rounded-xl border border-white/[0.06] bg-[#121417] px-3 py-2.5">
                    <span className="flex items-center justify-between text-[11px] text-[#A1A8B3]">
                      {label} <span className="font-mono text-[#D8D2C4]">{(s[k] ?? dflt).toFixed(2)}</span>
                    </span>
                    <input
                      data-testid={`voice-${k}`}
                      type="range"
                      min={min}
                      max={max}
                      step={step}
                      value={s[k] ?? dflt}
                      onChange={(e) => field(k)(Number(e.target.value))}
                      className="mt-2 w-full accent-[#E07A5F]"
                    />
                  </label>
                ))}
              </div>
              </>
            )}

            <p className="mt-7 text-sm font-medium text-[#D8D2C4]">
              The understudy
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6E7582]">
                {realVoice ? "only if the characters run out" : "speaking now"}
              </span>
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
              OpenAI's own voices. They cost nothing extra and never run out, but they are American voices
              performing a Kenyan accent. Press the speaker to hear one, then <span className="text-[#D8D2C4]">his</span> or
              <span className="text-[#D8D2C4]"> her</span> to cast it as the stand-in for that side.
            </p>
            <div className="mt-2 grid gap-2" data-testid="voice-audition">
              {(opts?.realtime_voices || [s.voice_male, s.voice_female]).map((v) => {
                const his = s.voice_male === v;
                const hers = s.voice_female === v;
                const tag = `cast-${v}`;
                return (
                  <div
                    key={v}
                    data-testid={`voice-row-${v}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${his || hers ? "border-white/25 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"}`}
                  >
                    <button
                      data-testid={`voice-hear-${v}`}
                      onClick={() => hear(null, v, hers && !his ? s.voice_direction_female : s.voice_direction, tag)}
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/20 text-[#A1A8B3] hover:border-[#E07A5F]/50 hover:text-[#F4A261]"
                      aria-label={playing === tag ? `Stop ${v}` : `Hear ${v}`}
                    >
                      {loadingVoice === tag ? <Loader2 size={14} className="animate-spin" /> : playing === tag ? <Square size={12} /> : <Volume2 size={14} />}
                    </button>
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-[13px] text-[#F3EFE6]">{v}</span>
                      <span className="block text-[11px] leading-snug text-[#6E7582]">{VOICE_NOTES[v] || "—"}</span>
                    </span>
                    <span className="flex shrink-0 gap-1.5">
                      {[["male", "his", his, "voice_male"], ["female", "her", hers, "voice_female"]].map(([who, word, on, key]) => (
                        <button
                          key={who}
                          data-testid={`voice-cast-${who}-${v}`}
                          aria-pressed={on}
                          onClick={() => pset({ [key]: v })}
                          className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${on ? "border-white/40 bg-white/10 text-[#F3EFE6]" : "border-white/10 text-[#6E7582] hover:border-[#E07A5F]/50 hover:text-[#A1A8B3]"}`}
                        >
                          {word}
                        </button>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>

            <p className="mt-7 text-sm font-medium text-[#D8D2C4]">2 — How the understudy speaks it</p>
            <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
              {realVoice
                ? "Only the understudy reads these notes. Keith and Yna need none — they are already Kenyan. If the real voices ever hand over, this is how the stand-in is told to perform."
                : "Written to the performer, not to the participant. It changes accent, pace and mood — never the words Griot chooses."}
              {" "}Each side has its own note, because the woman's voice should not be told to perform as an old man.
            </p>
            <div className={`mt-3 grid gap-6 ${realVoice ? "opacity-60" : ""}`}>
              {SIDES.map(([who, label, vKey, dKey]) => (
                <div key={who} className="min-w-0" data-testid={`voice-direction-${who}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#F4A261]">{label}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {Object.entries(opts?.directions || {}).map(([k, d]) => (
                      <button
                        key={k}
                        data-testid={`voice-preset-${who}-${k}`}
                        onClick={() => field(dKey)(d.text)}
                        className={`rounded-full border px-2.5 py-1 text-[10.5px] transition-colors ${(s[dKey] || "") === d.text ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#F4A261]" : "border-white/10 text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261]"}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid={`voice-direction-input-${who}`}
                    value={s[dKey] || ""}
                    onChange={(e) => field(dKey)(e.target.value)}
                    rows={5}
                    maxLength={1500}
                    placeholder="Speak English with a Kenyan cadence. Unhurried. Leave a real pause where a thought turns…"
                    className="mt-2.5 w-full resize-y rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[12.5px] leading-relaxed text-[#F3EFE6] placeholder:text-[#4F5560] focus:border-[#E07A5F] focus:outline-none"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <button
                      data-testid={`voice-direction-hear-${who}`}
                      onClick={() => hear(null, s[vKey], s[dKey], `${who}-${s[vKey]}`)}
                      className="inline-flex items-center gap-2 rounded-full border border-[#E07A5F]/50 px-3.5 py-1.5 text-[11px] text-[#F4A261] hover:bg-[#E07A5F]/10"
                    >
                      {loadingVoice === `${who}-${s[vKey]}` ? <Loader2 size={13} className="animate-spin" /> : playing === `${who}-${s[vKey]}` ? <Square size={11} /> : <Volume2 size={13} />}
                      Hear {s[vKey]} with this
                    </button>
                    <span className="font-mono text-[10.5px] text-[#6E7582]" data-testid={`voice-direction-count-${who}`}>{(s[dKey] || "").length}/1500</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <p className="text-sm font-medium text-[#D8D2C4]">3 — The words, not just the sound</p>
              <p className="mt-0.5 text-xs text-[#6E7582]">An accent is carried more by vocabulary and rhythm than by timbre. This makes Griot speak Kenyan English — "me, I think…", "…or how?", a Kiswahili word dropped in — which reads far more Kenyan than any voice setting. Free, and no delay.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="code-switch-picker">
                {Object.entries(opts?.code_switch || { light: "Kenyan English, lightly" }).map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`code-switch-${k}`}
                    onClick={() => field("code_switch")(k)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors ${(s.code_switch || "light") === k ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#F4A261]" : "border-white/10 text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7">
              <p className="text-sm font-medium text-[#D8D2C4]">4 — The tradition he thinks in</p>
              <p className="mt-0.5 text-xs text-[#6E7582]">How much of the griot is load-bearing: Ubuntu as his way of seeing (a person is a person through other people), methali used instead of explanations, a parable before an argument, call-and-response, praise and needle. Deep makes this the strongest layer in his head, above the dials.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="tradition-picker">
                {Object.entries(opts?.tradition || { deep: "Deep griot" }).map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`tradition-${k}`}
                    onClick={() => field("tradition")(k)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors ${(s.tradition || "deep") === k ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#F4A261]" : "border-white/10 text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <Pick label="Speech model (typed replies only)" testId="tts-model-select" value={s.tts_model} onChange={field("tts_model")} options={(models?.tts || [s.tts_model]).map((m) => [m, m])} />
              <Pick label="Default language" testId="language-select" value={s.language} onChange={field("language")} options={Object.entries(opts?.languages || { auto: "Auto-detect" })} />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-[#6E7582]">
              Nothing here is live until you press <span className="text-[#D8D2C4]">Publish</span> at the top.
              {realVoice
                ? ` The conversation runs on ${s.realtime_model || "the realtime model"} for its ears and its interruption, and every word is spoken by ${s.eleven_voice_male_name || "the man's voice"} or ${s.eleven_voice_female_name || "the woman's voice"}.`
                : ` The conversation runs on ${s.realtime_model || "the realtime model"}, which both hears and speaks, and takes the direction above.`}
            </p>
          </Card>

          <Card
            show={tab === "voice"}
            title="Models"
            note={models ? `${models.chat.length} chat models available on your OpenAI key.` : "Reading the models your OpenAI key can use…"}
            testId="model-panel"
            right={
              <button data-testid="model-probe-btn" onClick={probe} disabled={probing} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6] disabled:opacity-60">
                {probing ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />} Test speed
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Pick label="Chat model" testId="chat-model-select" value={s.chat_model} onChange={field("chat_model")} options={(models?.chat || [s.chat_model]).map((m) => [m, label(m)])} />
              <Pick label="Transcription model" testId="stt-model-select" value={s.stt_model} onChange={field("stt_model")} options={(models?.stt || [s.stt_model]).map((m) => [m, m])} />
            </div>
            {probeInfo && (
              <p
                data-testid="model-probe-result"
                className={`mt-3 rounded-xl border px-3 py-2 text-xs ${probeInfo.ok === false ? "border-[#E63946]/40 bg-[#E63946]/10 text-[#E63946]" : probeInfo.quirks?.length ? "border-[#E9C46A]/30 bg-[#E9C46A]/10 text-[#E9C46A]" : "border-[#2EC4B6]/30 bg-[#2EC4B6]/10 text-[#2EC4B6]"}`}
              >
                {probeInfo.ok === false
                  ? `${s.chat_model} refused a test call — pick another model. ${probeInfo.error || ""}`
                  : `${s.chat_model} answered a one-word prompt in ${(probeInfo.ms / 1000).toFixed(1)}s${probeInfo.quirks?.length ? ` · ${probeInfo.quirks.join(", ")}` : ""}.`}
              </p>
            )}
            <p className="mt-2 text-xs text-[#6E7582]">Speed here is the model's own latency on a trivial prompt — a slow model makes every spoken reply slower. Griot adapts automatically to models that refuse our settings.</p>
          </Card>
        </div>

        <div className={`min-w-0 space-y-5 ${cols.right ? (cols.left ? "xl:col-span-3" : "xl:col-span-5") : "hidden"}`}>
          <Card
            show={tab === "welcome"}
            title="Ways in"
            note={waysCustom ? "Written by you. Participants see these, in this order — leave it empty to show none." : "Currently drafted by Griot from this Lab's memory. Edit or save to take control."}
            testId="openings-editor"
            right={
              <button data-testid="openings-suggest-btn" onClick={suggest} disabled={suggesting} className="inline-flex items-center gap-2 rounded-full border border-[#E07A5F]/50 px-4 py-2 text-sm text-[#F4A261] hover:bg-[#E07A5F]/10 disabled:opacity-60">
                {suggesting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Suggest from memory
              </button>
            }
          >
            {ways.length === 0 && <p className="text-sm text-[#6E7582]" data-testid="openings-empty">No questions — participants will just be invited to speak.</p>}
            <ul className="space-y-2">
              {ways.map((w, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-6 shrink-0 font-mono text-[11px] text-[#6E7582]">{String(i + 1).padStart(2, "0")}</span>
                  <input
                    data-testid={`opening-input-${i + 1}`}
                    value={w}
                    onChange={(e) => setWay(i, e.target.value)}
                    maxLength={120}
                    placeholder="A question a participant could begin with"
                    className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-[#F3EFE6] placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none"
                  />
                  <button data-testid={`opening-up-${i + 1}`} onClick={() => moveWay(i, -1)} disabled={i === 0} aria-label="Move up" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-[#A1A8B3] hover:text-[#F3EFE6] disabled:opacity-30"><ArrowUp size={13} /></button>
                  <button data-testid={`opening-down-${i + 1}`} onClick={() => moveWay(i, 1)} disabled={i === ways.length - 1} aria-label="Move down" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-[#A1A8B3] hover:text-[#F3EFE6] disabled:opacity-30"><ArrowDown size={13} /></button>
                  <button data-testid={`opening-remove-${i + 1}`} onClick={() => setWays((x) => x.filter((_, j) => j !== i))} aria-label="Remove" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-[#A1A8B3] hover:border-[#E63946]/50 hover:text-[#E63946]"><Trash2 size={13} /></button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button data-testid="opening-add-btn" onClick={() => setWays((x) => [...x, ""])} disabled={ways.length >= 5} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6] disabled:opacity-40">
                <Plus size={14} /> Add a question
              </button>
              <span className="font-mono text-[11px] text-[#6E7582]">{ways.length}/5 · saved when you publish</span>
            </div>
          </Card>

          <Card
            show={tab === "welcome"}
            title="The welcome"
            note="Griot picks a different one of these each time someone opens a flowing conversation, and never repeats within a sitting. Each line opens in Kiswahili and turns into English — Griot speaks the Kiswahili as a Kenyan would and never translates itself. Speech is cached, so it starts instantly."
            testId="greeting-editor"
            right={
              <button data-testid="greeting-suggest-btn" onClick={() => suggestWelcomes("auto")} disabled={suggestingWelcome} className="inline-flex items-center gap-2 rounded-full border border-[#E07A5F]/50 px-4 py-2 text-sm text-[#F4A261] hover:bg-[#E07A5F]/10 disabled:opacity-60">
                {suggestingWelcome ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Draft from memory
              </button>
            }
          >
            <div className="space-y-3">
              {(s.greetings || []).map((g, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`welcome-row-${i}`}>
                  <span className="mt-3 font-mono text-[10px] text-[#6E7582]">{String(i + 1).padStart(2, "0")}</span>
                  <textarea
                    data-testid={`welcome-input-${i}`}
                    value={g}
                    onChange={(e) => setWelcome(i, e.target.value)}
                    rows={2}
                    maxLength={600}
                    className="min-w-0 flex-1 resize-y rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm leading-relaxed text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
                  />
                  <button title="Hear it" data-testid={`welcome-hear-${i}`} onClick={() => hear(g)} disabled={!!loadingVoice} className="mt-1 rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] disabled:opacity-40"><Volume2 size={14} /></button>
                  <button title="Remove" data-testid={`welcome-remove-${i}`} onClick={() => setWelcomes((s.greetings || []).filter((_, j) => j !== i))} className="mt-1 rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] hover:text-[#E63946]"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button data-testid="welcome-add-btn" onClick={() => setWelcomes([...(s.greetings || []), ""])} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6]">
                <Plus size={14} /> Add a welcome
              </button>
              <span className="font-mono text-[11px] text-[#6E7582]">{(s.greetings || []).length} · saved when you publish</span>
            </div>
            <p className="mt-2 text-xs text-[#6E7582]">Leave the list empty to open in silence. Changing the words re-renders the audio on the next conversation.</p>
          </Card>

          <Card
            show={tab === "welcome"}
            title="The welcome, English only"
            note="What he opens with when a visitor sets the conversation to English. Same old man, same weight — not a word of Kiswahili, and any old saying is said in plain English. Leave this empty and an English-only visitor will still hear the Kiswahili welcome above."
            testId="greeting-en-editor"
            right={
              <button data-testid="greeting-en-suggest-btn" onClick={() => suggestWelcomes("en")} disabled={suggestingWelcome} className="inline-flex items-center gap-2 rounded-full border border-[#E07A5F]/50 px-4 py-2 text-sm text-[#F4A261] hover:bg-[#E07A5F]/10 disabled:opacity-60">
                {suggestingWelcome ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Draft from memory
              </button>
            }
          >
            <div className="space-y-3">
              {(s.greetings_en || []).map((g, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`welcome-en-row-${i}`}>
                  <span className="mt-3 font-mono text-[10px] text-[#6E7582]">{String(i + 1).padStart(2, "0")}</span>
                  <textarea
                    data-testid={`welcome-en-input-${i}`}
                    value={g}
                    onChange={(e) => setWelcome(i, e.target.value, "greetings_en")}
                    rows={2}
                    maxLength={600}
                    className="min-w-0 flex-1 resize-y rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm leading-relaxed text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
                  />
                  <button title="Hear it" data-testid={`welcome-en-hear-${i}`} onClick={() => hear(g)} disabled={!!loadingVoice} className="mt-1 rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] disabled:opacity-40"><Volume2 size={14} /></button>
                  <button title="Remove" data-testid={`welcome-en-remove-${i}`} onClick={() => setWelcomes((s.greetings_en || []).filter((_, j) => j !== i), "greetings_en")} className="mt-1 rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] hover:text-[#E63946]"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button data-testid="welcome-en-add-btn" onClick={() => setWelcomes([...(s.greetings_en || []), ""], "greetings_en")} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-[#A1A8B3] hover:border-white/30 hover:text-[#F3EFE6]">
                <Plus size={14} /> Add an English welcome
              </button>
              <span className="font-mono text-[11px] text-[#6E7582]">{(s.greetings_en || []).length} · saved when you publish</span>
            </div>
          </Card>

          <Card show={tab === "persona"} title="Try it" note="Runs against this Lab's live memory with your unpublished sliders." testId="try-it-sandbox">
            <input data-testid="sandbox-question-input" value={q} onChange={(e) => setQ(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#D8D2C4] focus:border-[#E07A5F] focus:outline-none" />
            <button data-testid="sandbox-run-btn" onClick={run} disabled={running} className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-[#E07A5F]/50 px-4 py-2 text-sm text-[#F4A261] hover:bg-[#E07A5F]/10 disabled:opacity-60">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run against live memory
            </button>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-white/[0.06] p-4">
                <p className="eyebrow mb-2">Live · v{data.live.version}</p>
                <p className="text-sm leading-relaxed text-[#A1A8B3]" data-testid="sandbox-live-output">{out?.live || "—"}</p>
              </div>
              <div className="rounded-xl border border-[#E07A5F]/30 bg-[#E07A5F]/5 p-4">
                <p className="eyebrow mb-2">Draft</p>
                <p className="text-sm leading-relaxed text-[#F3EFE6]" data-testid="sandbox-draft-output">{out?.draft || "Run the question to compare the draft persona with the live one."}</p>
              </div>
            </div>
          </Card>

          <Card show={tab === "limits"} title="Limits & spend" note="Hard stops. When a cap is hit, new sessions and turns are refused and participants see the 'come back later' screen." testId="spend-caps">
            <div className="flex justify-between text-xs text-[#A1A8B3]"><span>Month to date (estimated)</span><span className="font-mono">${spent.toFixed(2)} / ${s.monthly_cap_usd}</span></div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-gradient-to-r from-[#E07A5F] to-[#F4A261]" style={{ width: `${Math.min(100, (spent / (s.monthly_cap_usd || 1)) * 100)}%` }} /></div>
            <div className="mt-5 grid gap-3 sm:grid-cols-4">
              <Num label="Daily cap" value={s.daily_cap_usd} suffix="USD" testId="daily-cap-input" onChange={num("daily_cap_usd")} />
              <Num label="Monthly cap" value={s.monthly_cap_usd} suffix="USD" testId="monthly-cap-input" onChange={num("monthly_cap_usd")} />
              <Num label="Turns per session" value={s.turns_per_session} suffix="turns" testId="turns-per-session-input" onChange={num("turns_per_session")} />
              <Num label="Retrieved chunks" value={s.top_k} suffix="top-K" testId="top-k-input" onChange={num("top_k")} />
            </div>
          </Card>

          <Card show={tab === "limits"} title="What participants see" note="Griot is voice-first. Nothing is written on screen except the few words Griot chooses to surface." testId="participant-view-panel">
            <Toggle
              testId="allow-text-toggle"
              label="Offer a typed conversation"
              note="On, a visitor can choose to type instead of speak — on the sit-down screen or mid-conversation. Griot writes his reply and stays silent, and the voice line is never opened, so a typed sitting costs a fraction of a spoken one. Turn it off for a voice-only room."
              checked={s.allow_text !== false}
              onChange={field("allow_text")}
            />
            <Toggle
              testId="show-notes-toggle"
              label="Griot's notes on screen"
              note="Griot can put a few words on screen — the idea underneath what it just said, a fragment of a quote, a name, or a numbered set when it lays out a plan. It holds while Griot speaks the point, then clears itself. It decides when; it costs nothing extra."
              checked={s.show_notes !== false}
              onChange={field("show_notes")}
            />
            <Toggle
              testId="note-motion-toggle"
              label="Notes move on screen"
              note="On, the words surface word by word, hang beside the form while the point is spoken, then fly across the stage and dissolve. Off, they simply appear and fade where they stand — calmer, and better on a projector or a slow device."
              checked={s.note_motion !== false}
              onChange={field("note_motion")}
            />
            <Toggle
              testId="ask-name-toggle"
              label="Ask for their first name"
              note="On, Griot's welcome ends by asking what to call them, and he uses the name sparingly after that — nothing is stored unless they chose to be archived. Off, he never asks and calls them 'mgeni wangu'."
              checked={s.ask_name !== false}
              onChange={field("ask_name")}
            />
            <Toggle
              testId="show-transcript-toggle"
              label="Answer text on screen"
              note="Off is pure voice — Griot speaks and nothing is written. Turn it on for noisy rooms and deaf or hard-of-hearing participants."
              checked={s.show_transcript}
              onChange={field("show_transcript")}
            />
            <Toggle
              testId="show-greeting-toggle"
              label="Welcome line on screen"
              note="Off keeps the opening purely spoken. On also prints the welcome under the form."
              checked={s.show_greeting}
              onChange={field("show_greeting")}
            />
          </Card>
        </div>
      </div>

      <Sheet open={showHistory} onOpenChange={setShowHistory}>
        <SheetContent side="right" className="w-full border-white/10 bg-[#121417] text-[#F3EFE6] sm:max-w-xl" data-testid="persona-version-history">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-[#F3EFE6]">Published versions</SheetTitle>
          </SheetHeader>
          <p className="mt-1 text-xs text-[#6E7582]">Each version holds the persona and the configuration it ran with. Rolling back restores both.</p>
          <ul className="mt-5 max-h-[calc(100vh-150px)] space-y-2 overflow-y-auto pr-1">
            {data.versions.map((v) => (
              <li key={v.id} className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-[#F4A261]">v{v.version}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[#D8D2C4]">{v.note}</span>
                  {v.live ? (
                    <span className="rounded-full bg-[#2EC4B6]/15 px-2.5 py-1 font-mono text-[10px] text-[#2EC4B6]">LIVE</span>
                  ) : (
                    <button data-testid={`rollback-v${v.version}`} onClick={() => rollback(v)} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-xs text-[#A1A8B3] hover:border-[#E07A5F]/60 hover:text-[#F4A261]"><RotateCcw size={12} /> Rollback</button>
                  )}
                </div>
                <p className="mt-2 font-mono text-[10px] text-[#6E7582]">W{v.traits.warmth} C{v.traits.curiosity} F{v.traits.formality} D{v.traits.directness} P{v.traits.poetic} E{v.traits.engagement ?? 45} H{v.traits.humor ?? 30}{v.brief_chars ? ` · brief ${v.brief_chars} chars` : " · no brief"}</p>
                {v.settings ? (
                  <p className="mt-1 font-mono text-[10px] text-[#6E7582]">
                    {v.settings.chat_model} · {v.settings.tts_voice} · {v.settings.language} · ${v.settings.daily_cap_usd}/day · {v.settings.turns_per_session} turns · top-{v.settings.top_k}
                  </p>
                ) : (
                  <p className="mt-1 font-mono text-[10px] text-[#6E7582]">no configuration captured — rollback restores the persona only</p>
                )}
                <p className="mt-1 text-[11px] text-[#6E7582]">{v.author} · {new Date(v.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
    </div>
  );
}
